import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const ReadOnlyDataSection = ({ cardType, readOnlyData }) => {
  const [expanded, setExpanded] = useState(false);

  if (!readOnlyData || typeof readOnlyData !== 'object') return null;

  const readings = Array.isArray(readOnlyData.readings) ? readOnlyData.readings : null;
  const weights = Array.isArray(readOnlyData.weights) ? readOnlyData.weights : null;
  const idealWeightGain = Array.isArray(readOnlyData.idealWeightGain)
    ? readOnlyData.idealWeightGain
    : null;

  const totalHistoryCount =
    (readings?.length || 0) + (weights?.length || 0) + (idealWeightGain?.length || 0);

  if (totalHistoryCount === 0) return null;

  return (
    <Box sx={{ mt: 2.5, mb: 1 }}>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: '1px solid #E2E8F0',
          bgcolor: '#F8FAFC',
          '&:before': { display: 'none' },
          overflow: 'hidden',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: '#64748B' }} />}
          sx={{
            minHeight: 44,
            px: 2,
            py: 0.5,
            bgcolor: '#F1F5F9',
            '& .MuiAccordionSummary-content': { my: 0.5 },
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <HistoryIcon sx={{ fontSize: 18, color: '#64748B' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
              Historical Records & Context ({totalHistoryCount} entries)
            </Typography>
            <Chip
              label={expanded ? 'Hide History' : 'View History'}
              size="small"
              sx={{
                height: 20,
                fontSize: 11,
                bgcolor: '#FFFFFF',
                color: '#475569',
                border: '1px solid #CBD5E1',
              }}
            />
          </Stack>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 2, bgcolor: '#FFFFFF' }}>
          {/* Vitals Readings Table (BP, SpO2, Pulse, Temp) */}
          {readings && readings.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', mb: 1, display: 'block' }}>
                PAST VITAL READINGS
              </Typography>
              <TableContainer sx={{ maxHeight: 220, border: '1px solid #E2E8F0', borderRadius: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Week</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Reading</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Source</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {readings.map((r, i) => {
                      let readingValue = '-';
                      if (r.systolic !== undefined && r.diastolic !== undefined) {
                        readingValue = `${r.systolic}/${r.diastolic} ${r.unit || 'mmHg'}`;
                      } else if (r.spo2 !== undefined) {
                        readingValue = `${r.spo2} %`;
                      } else if (r.pulse !== undefined) {
                        readingValue = `${r.pulse} bpm`;
                      } else if (r.temperature !== undefined) {
                        readingValue = `${r.temperature} ${r.unit || '°F'}`;
                      }

                      return (
                        <TableRow key={r.id || i} hover>
                          <TableCell sx={{ fontSize: 12 }}>{formatDate(r.createdAt)}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>
                            {r.week !== undefined ? `Week ${r.week}` : '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{readingValue}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>
                            {r.isUpdatedByAi ? (
                              <Chip
                                icon={<AutoAwesomeIcon sx={{ fontSize: '12px !important' }} />}
                                label="AI Captured"
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: 10,
                                  bgcolor: '#F3E8FF',
                                  color: '#7C3AED',
                                }}
                              />
                            ) : (
                              <Typography variant="caption" sx={{ color: '#64748B' }}>
                                Clinical Record
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Weight Gain History */}
          {weights && weights.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', mb: 1, display: 'block' }}>
                RECORDED PATIENT WEIGHTS
              </Typography>
              <TableContainer sx={{ maxHeight: 200, border: '1px solid #E2E8F0', borderRadius: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Week</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Trimester</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Weight</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Source</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {weights.map((w, idx) => (
                      <TableRow key={w.id || idx} hover>
                        <TableCell sx={{ fontSize: 12 }}>{formatDate(w.createdAt)}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{w.week !== undefined ? `Week ${w.week}` : '-'}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{w.trimester || '-'}</TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>
                          {w.weight} {w.unit || 'kg'}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {w.isUpdatedByAi ? (
                            <Chip
                              icon={<AutoAwesomeIcon sx={{ fontSize: '12px !important' }} />}
                              label="AI Captured"
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: 10,
                                bgcolor: '#F3E8FF',
                                color: '#7C3AED',
                              }}
                            />
                          ) : (
                            <Typography variant="caption" sx={{ color: '#64748B' }}>
                              Clinical Record
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Ideal Weight Gain Reference (WHO) */}
          {idealWeightGain && idealWeightGain.length > 0 && (
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <TrendingUpIcon sx={{ fontSize: 16, color: '#059669' }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B' }}>
                  WHO IDEAL WEIGHT GAIN GUIDELINES
                </Typography>
              </Stack>
              <TableContainer sx={{ border: '1px solid #E2E8F0', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Week</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Trimester</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Ideal Gain Range</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Standard</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {idealWeightGain.map((g, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontSize: 12 }}>Week {g.week}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{g.trimester}</TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>
                          {g.min} - {g.max} {g.unit || 'kg'}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {g.isWHO ? (
                            <Chip label="WHO Standard" size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#ECFDF5', color: '#059669' }} />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ReadOnlyDataSection;
